import apiClient from '../apiClient';
import type { ApiResponse, User, PaginationMeta } from '@/types';
import { getUrlWithQueryParams } from '@/lib/utils';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactQueryKeys } from '../reactQueryKeys';
import { ReactMutationKeys } from '../reactMutationKeys';

const ADMIN_URL = '/admin/users';

interface PaginatedResponse {
  items: User[];
  pagination: PaginationMeta;
}

export const getUsers = async (
  page?: number,
  limit?: number,
  search?: string,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc',
  role?: string,
  companyId?: string,
  status?: string,
  betaTester?: boolean
): Promise<PaginatedResponse> => {
  const url = getUrlWithQueryParams(ADMIN_URL, {
    page,
    limit,
    search,
    sortBy,
    sortOrder,
    role,
    companyId,
    status,
    betaTester,
  });
  const { data } = await apiClient.get<ApiResponse<User[]>>(url);
  return {
    items: data.data,
    pagination: data.meta!,
  };
};

export const getUser = async (id: string): Promise<User> => {
  const { data } = await apiClient.get<ApiResponse<User>>(`${ADMIN_URL}/${id}`);
  return data.data;
};

export const createUser = async (user: Partial<User> & { password?: string }): Promise<User> => {
  const { data } = await apiClient.post<ApiResponse<User>>(ADMIN_URL, user);
  return data.data;
};

export const updateUser = async (id: string, user: Partial<User>): Promise<User> => {
  const { data } = await apiClient.put<ApiResponse<User>>(`${ADMIN_URL}/${id}`, user);
  return data.data;
};

export const deleteUser = async (id: string): Promise<void> => {
  await apiClient.delete(`${ADMIN_URL}/${id}`);
};

export const resetPassword = async (id: string, newPassword: string): Promise<void> => {
  await apiClient.post(`${ADMIN_URL}/${id}/reset-password`, { newPassword });
};

/** L-01: the per-user beta-tester override (audited on the API as `user.beta_tester`). */
export const setBetaTester = async (
  id: string,
  isBetaTester: boolean
): Promise<{ id: string; isBetaTester: boolean; changed: boolean }> => {
  const { data } = await apiClient.put<ApiResponse<{ id: string; isBetaTester: boolean; changed: boolean }>>(
    `${ADMIN_URL}/${id}/beta-tester`,
    { isBetaTester }
  );
  return data.data;
};

export const assignUserToCompany = async (userId: string, companyId: string | null): Promise<User> => {
  const { data } = await apiClient.put<ApiResponse<User>>(`${ADMIN_URL}/${userId}/assign-company`, { companyId });
  return data.data;
};

// React Query Hooks

export const useGetUsers = (
  page?: number,
  limit?: number,
  search?: string,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc',
  role?: string,
  companyId?: string,
  status?: string,
  betaTester?: boolean
) =>
  useQuery({
    queryKey: [ReactQueryKeys.GET_USERS, page, limit, search, sortBy, sortOrder, role, companyId, status, betaTester],
    queryFn: () => getUsers(page, limit, search, sortBy, sortOrder, role, companyId, status, betaTester),
  });

export const useGetUser = (id: string) =>
  useQuery({
    queryKey: [ReactQueryKeys.GET_USER, id],
    queryFn: () => getUser(id),
    enabled: !!id,
  });

export const useCreateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (user: Partial<User> & { password?: string }) => createUser(user),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ReactQueryKeys.GET_USERS] });
    },
    mutationKey: [ReactMutationKeys.CREATE_USER],
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...user }: Partial<User> & { id: string }) => updateUser(id, user),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ReactQueryKeys.GET_USERS] });
      queryClient.invalidateQueries({ queryKey: [ReactQueryKeys.GET_USER] });
    },
    mutationKey: [ReactMutationKeys.UPDATE_USER],
  });
};

export const useSetBetaTester = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isBetaTester }: { id: string; isBetaTester: boolean }) => setBetaTester(id, isBetaTester),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ReactQueryKeys.GET_USERS] });
      queryClient.invalidateQueries({ queryKey: [ReactQueryKeys.GET_USER] });
    },
    mutationKey: [ReactMutationKeys.SET_BETA_TESTER],
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ReactQueryKeys.GET_USERS] });
    },
    mutationKey: [ReactMutationKeys.DELETE_USER],
  });
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: ({ id, newPassword }: { id: string; newPassword: string }) =>
      resetPassword(id, newPassword),
    mutationKey: [ReactMutationKeys.RESET_PASSWORD],
  });
};

// Push Notifications
export const getUserDeviceTokens = async (userId: string): Promise<{ tokens: any[]; count: number }> => {
  const { data } = await apiClient.get<ApiResponse<{ tokens: any[]; count: number }>>(`/push-notifications/tokens/${userId}`);
  return data.data;
};

export const sendNotificationToUser = async (
  userId: string,
  payload: { title: string; body: string; data?: Record<string, string> }
): Promise<any> => {
  const { data } = await apiClient.post<ApiResponse<any>>(`/push-notifications/send/${userId}`, payload);
  return data.data;
};

export const useSendNotification = () => {
  return useMutation({
    mutationFn: ({ userId, ...payload }: { userId: string; title: string; body: string; data?: Record<string, string> }) =>
      sendNotificationToUser(userId, payload),
  });
};

export const useAssignUserToCompany = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, companyId }: { userId: string; companyId: string | null }) =>
      assignUserToCompany(userId, companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ReactQueryKeys.GET_USERS] });
      queryClient.invalidateQueries({ queryKey: [ReactQueryKeys.GET_COMPANY] });
    },
    mutationKey: [ReactMutationKeys.ASSIGN_USER_TO_COMPANY],
  });
};
